import React, { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import styles from "./TransAuth.module.scss";

// import { fetchSaveVerifyInfoSuccess } from "store/actions/appActions";
import OtpInput from "../../elements/Inputs/OtpInput";
import PinInput from "../../elements/Inputs/PinInput";
import { TradeAuthType } from "@/stores/dataStore/memoryStore/const/constIndex";
import ToastUtil from "../Toast/Toast";
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Stack, Switch, useTheme } from "@mui/material";
import Grid from '@mui/material/Grid2';
import ButtonElement from "@/views/elements/button/ButtonElement";
import { getListCA, submitSignData, Type_CA, useTradeAuthenticated } from "@/controllers/gridManager/appManager/appManager";
import { doResendOTP, useUserInfo } from "@/controllers/gridManager/accountsManager/accountManager";
import { useTranslation } from "react-i18next";
import TypographyElement from "@/views/elements/typography/TypographyElement";
import { debounce } from "lodash";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { setSaveVerifyAction } from "@/stores/dataManager/Actions/appAction";
import SelectElement, { ItemOption } from "@/views/elements/select/SelectElement";

const TIME_COUNTDOWN = 60

export type AuthComponentRef = {
    processAuth: () => void;
}

let intervalOTP: any = null

const TransAuth = React.forwardRef<AuthComponentRef, any>(
    (
        {
            autoFocus,
            noButton,
            onFinish,
            listAuthType,
            defaultAuthType,
            onAuthCodeChange, // Called when want to control the authCode value from the outside TransAuth2
            doProcessData, // Contain data submitting
            twoFactorAuthFunc, // Call when submit data
            setLoadStatus,
            onCancel, // Call when click cancel button
            transactionId,
            tokenId,
            noSaveAuth, // Có hiển thị lựa chọn lưu mã xác thực cho lần sau không
            // saveAuthentication = false, // Lưu mã xác thực cho lần sau: mặc định không
            toggleSaveAuthentication, // Thay đổi trạng thái lưu mã xác thực

            preAuthCb, // Gọi trước khi gọi api submit data
            successAuthCb, // Handle logic after doProcessAuth success
            failAuthCb, // Handle logic after doProcessAuth fail
            disableSaveAuth = false,
            internalToast = false,
            autoSend = false,
            verified
        }: any,
        ref
    ) => {

        useImperativeHandle(ref, () => ({
            processAuth: () => processAuth(),
        }));

        const { t } = useTranslation()
        const theme = useTheme()

        // Trạng thái lưu xác thực do server trả về
        const { saveVerify }: any = useTradeAuthenticated()
        const { userInfo } = useUserInfo()
        const { username } = userInfo

        // Trạng thái chung cho tất cả các nút xác thực (phải đồng bộ với bên fs2)
        const [microAppSaveAuthentication, setMicroAppSaveAuthentication] = useState(saveVerify);


        // Thiết lập authType mặc định khi cho phép chọn authType
        const [authType, setAuthType] = useState(defaultAuthType);

        const [smartOtp, setSmartOtp] = useState("");
        const [pin, setPin] = useState("");
        const [otp, setOtp] = useState("");
        const [listCA, setListCA] = useState([]);
        const [currentCA, setCurrentCA] = useState(null);
        const [pinCA, setPinCA] = useState("");

        const countRef = useRef<number>(TIME_COUNTDOWN)
        const [count, setCount] = useState<number>()

        const [showPin, setShowPin] = useState(false);

        const [currTokenId, setCurrTokenId] = useState(tokenId);
        const [currTransactionId, setCurrTransactionId] = useState(transactionId);

        const toggleShowPin = () => setShowPin((prev) => !prev);

        const [disabledSend, setDisabledSend] = useState<boolean>(false)

        const handleChangeMicroAppSaveAuthStatus = () => {
            // dispatch(toggleSaveIbondAuthentication());
            setMicroAppSaveAuthentication((prev: boolean) => !prev);
            typeof toggleSaveAuthentication === "function" && toggleSaveAuthentication();
        }

        const disabledAuth = useMemo(() => {
            if (disableSaveAuth) return false;

            return saveVerify;
        }, [disableSaveAuth, saveVerify]);

        useEffect(() => {
            async function callApiGetListCA() {
                const { isSuccess, data } = await getListCA()
                if (isSuccess) {
                    const listForOption: [] = data.map((item: Type_CA): Partial<ItemOption> => {
                        return {
                            label: item?.caName ?? '',
                            value: item?.Serial ?? '',
                            ...item
                        }
                    })

                    setListCA(listForOption)
                    setCurrentCA(data?.[0]?.value ?? null)
                }
            }

            if (authType === TradeAuthType.CTS) {
                callApiGetListCA()
            }

            return () => {
            }
        }, [authType])


        useEffect(() => {
            setCurrTransactionId(transactionId);
        }, [transactionId])

        useEffect(() => {
            setCurrTokenId(tokenId);
        }, [tokenId])

        useEffect(() => {
            if (verified !== undefined) {
                setSaveVerifyAction(verified)
            }
        }, [verified])

        const handleChangeSmartOtp = (val: string) => {
            setSmartOtp(val);
            onAuthCodeChange(val);
        };

        const handleChangeOtp = (val: string) => {
            setOtp(val);
            onAuthCodeChange(val);
        };

        const handleChangePin = (val: string) => {
            setPin(val);
            onAuthCodeChange(val);
        };

        const handleChangePinCA = (val: string) => {
            setPinCA(val);
            onAuthCodeChange(val);
        };

        const handleChangeCurrentCA = (event: SelectChangeEvent<any>) => {
            const value = event.target.value ?? ''
            setCurrentCA(value);
        };

        const currentInfoCA: any = useMemo(() => {
            return listCA.find((item: Type_CA) => item.Serial === currentCA)
        }, [currentCA, listCA])

        // Validate/format dữ liệu trước khi thực hiện doProcessAuth
        const preProcessAuth = () => {
            return true;
        };

        const doProcessAuth = (code: string, tokenId: string, transactionId: string, extraData: any) => {
            // Nếu sử dụng pre, success, failAuthCb -> đưa logic setLoadStatus vào trong các hàm này, bỏ props setLoadStatus ở TransAuth2
            preAuthCb && preAuthCb(); // Gọi logic trước khi gửi: setLoadStatus, ...

            // case ko xử lý logic trong preAuthCb
            typeof setLoadStatus === "function" && setLoadStatus(true);

            typeof twoFactorAuthFunc === "function" && twoFactorAuthFunc({
                ...extraData,
                code: code,
                tokenid: tokenId,
                transactionId,
                isSaveVerify: microAppSaveAuthentication,
            })
                .then(({ data }: { data: unknown }) => {
                    // Gọi logic khi thành công: setLoadStatus, toast message, ...
                    typeof successAuthCb === "function" && successAuthCb(data);

                    // case ko xử lý logic trong successAuthCb
                    if (typeof setLoadStatus === "function") {
                        setLoadStatus(false);
                        internalToast && ToastUtil.success(t('copytrade.common.success'));
                    }

                    // Lưu xác thực cho các lần sau
                    // console.log("vien.do: twoFactorAuthFunc ", microAppSaveAuthentication);
                    // dispatch(fetchSaveVerifyInfoSuccess(microAppSaveAuthentication));
                })
                .catch((error: any) => {
                    // Gọi logic khi thất bại: setLoadStatus, toast message, ...
                    typeof failAuthCb == "function" && failAuthCb(error);

                    // case ko xử lý logic trong failAuthCb
                    if (typeof setLoadStatus === "function") {
                        setLoadStatus(false);
                        internalToast && ToastUtil.errorApi(error, t('copytrade.common.somethingWrong'));
                    }
                })
        };

        const processAuth = () => {
            const passValidate = preProcessAuth();

            if (!passValidate) return;

            let code = null
            let extraData = {};

            if (doProcessData) {
                if (typeof doProcessData === "function") {
                    // TODO: process data => extraData
                } else {
                    extraData = doProcessData;
                }
            }

            switch (authType) {
                case TradeAuthType.PIN:
                    code = pin;
                    doProcessAuth(code, currTokenId, currTransactionId, extraData);
                    break;
                case TradeAuthType.OTP:
                    code = otp;
                    doProcessAuth(code, currTokenId, currTransactionId, extraData);
                    break;
                case TradeAuthType.SMARTOTP:
                    code = smartOtp;
                    doProcessAuth(code, currTokenId, currTransactionId, extraData);
                    break;
                case TradeAuthType.CTS:
                    code = pinCA;
                    setLoadStatus(true);
                    submitSignData({ serial: currentCA, username, pin: code }).then((data: any) => {
                        setLoadStatus(false);
                        currentCA && doProcessAuth(data, currentCA, transactionId, extraData);
                    }).catch((error) => {
                        setLoadStatus(false);
                    })
                    break;
                case null:
                    code = '';
                    doProcessAuth(code, currTokenId, currTransactionId, extraData);
                    break;
                default:
                    break;
            }
        };

        useEffect(() => {
            if (disabledSend === true) {
                countRef.current = TIME_COUNTDOWN
                setCount(TIME_COUNTDOWN)

                intervalOTP = setInterval(() => {
                    if (countRef.current === 0) {
                        setDisabledSend(false)
                        intervalOTP && clearInterval(intervalOTP)
                    }
                    countRef.current = countRef.current - 1 >= 0 ? countRef.current - 1 : 0
                    setCount(countRef.current)
                }, 1000);
            }

            return () => {
                intervalOTP && clearInterval(intervalOTP)
            }
        }, [disabledSend])



        const mapAuthType = (itemType: string) => {
            const arrayAuthType = Object.values(TradeAuthType)
            const indexResult = arrayAuthType.findIndex(item => item === itemType)
            return Object.keys(TradeAuthType)[indexResult]
        }

        const handleAuthType = (event: SelectChangeEvent) => {
            setAuthType(event.target.value);
        };

        const genAuthCode = async () => {
            const { isSuccess, data } = await doResendOTP({ transactionId: currTransactionId, authtype: authType })
            // console.log('ha_check_sendOTP', data)
            if (isSuccess) {
                const { tokenid, transactionId } = data
                setDisabledSend(true)
                setCurrTokenId(tokenid)
                setCurrTransactionId(transactionId)
            }
        }

        const debounceSendOTP = debounce(() => genAuthCode(), 300)

        return (
            <Fragment>
                <div className={styles.transAuth2Container}>
                    <Stack
                        spacing={2}
                        direction={"column"}
                    >
                        {verified === false && <Box>
                            <Grid container alignItems={"center"} spacing={2} columns={24}>
                                <Grid size={8}>
                                    <Box
                                    >
                                        {t("copytrade.auth.authMethod")}
                                    </Box>
                                </Grid>
                                <Grid size={16}>
                                    <Box
                                    >
                                        <FormControl fullWidth >
                                            <Select
                                                labelId="authtype-select-label"
                                                id="authtype-select"
                                                value={authType}
                                                defaultValue={defaultAuthType}
                                                size='small'
                                                sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 }, background: theme.palette.background.input }}
                                                onChange={handleAuthType}
                                            >
                                                {
                                                    listAuthType.map((itemType: any, index: number) => {
                                                        return <MenuItem key={index} value={itemType}>{mapAuthType(itemType)}</MenuItem>
                                                    })
                                                }
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>}

                        {authType === TradeAuthType.PIN && (

                            <Box sx={{ flexGrow: 1 }}>
                                <Grid container alignItems={"center"} spacing={2} columns={24}>
                                    <Grid size={8}>
                                        <Box>
                                            <span>
                                                {t("copytrade.auth.password")}
                                            </span>
                                        </Box>
                                    </Grid>
                                    <Grid size={16}>
                                        <Box>
                                            <PinInput
                                                autoFocus={autoFocus}
                                                onChange={handleChangePin}
                                                toggleShowPin={toggleShowPin}
                                                showPin={showPin}
                                                value={!disabledAuth ? pin : "•••••••"}
                                                disabled={disabledAuth}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                        )}

                        {authType === TradeAuthType.OTP && (
                            <Grid container alignItems={"center"} spacing={2} columns={24} >
                                <Grid size={8}>
                                    <span >
                                        SMS-OTP
                                    </span>
                                </Grid>
                                <Grid size={16}>
                                    <OtpInput
                                        autoFocus={autoFocus}
                                        value={!disabledAuth ? otp : "•••••••"}
                                        disabled={disabledAuth}
                                        onChange={handleChangeOtp}
                                        hiddenPaddingRight={true}
                                        endAdornment={
                                            disabledSend ? <ButtonElement sx={{
                                            }} disabled={true} onClick={debounceSendOTP} customType="Transparent">
                                                <TypographyElement sx={{
                                                    whiteSpace: "nowrap",
                                                }} ColorElement="brand">{count}</TypographyElement>
                                            </ButtonElement> : <ButtonElement onClick={debounceSendOTP} customType="Transparent">
                                                <TypographyElement sx={{
                                                    textDecoration: "underline",
                                                    whiteSpace: "nowrap",
                                                }} ColorElement="brand">Lấy SMS-OTP</TypographyElement>
                                            </ButtonElement>
                                        }
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {authType === TradeAuthType.CTS && (
                            <Grid container alignItems={"center"} spacing={2} columns={24} >
                                <Grid size={24}>
                                    <span >
                                        {t("copytrade.auth.cts")}
                                    </span>
                                </Grid>

                                <Grid size={24}>
                                    {
                                        listCA.length !== 0 ? (
                                            <SelectElement
                                                listItems={listCA}
                                                value={currentCA}
                                                onChange={handleChangeCurrentCA}
                                            />
                                        ) : (
                                            <TypographyElement sx={{ paddingBottom: "10px" }} ColorElement="brand" TextAlignElement="center">{t("copytrade.auth.nocaloaded")}</TypographyElement>
                                        )
                                    }
                                </Grid>

                                {currentCA && <>
                                    <Grid size={8}>
                                        <span >
                                            {t("copytrade.auth.name")}
                                        </span>
                                    </Grid>
                                    <Grid size={16}>
                                        <TypographyElement>{currentInfoCA?.caName}</TypographyElement>
                                    </Grid>

                                    <Grid size={8}>
                                        <span >
                                            {t("copytrade.auth.serial")}
                                        </span>
                                    </Grid>
                                    <Grid size={16}>
                                        <TypographyElement>{currentInfoCA?.Serial}</TypographyElement>
                                    </Grid>

                                    <Grid size={8}>
                                        <span >
                                            {t("copytrade.auth.expire")}
                                        </span>
                                    </Grid>
                                    <Grid size={16}>
                                        <TypographyElement>{currentInfoCA?.expire}</TypographyElement>
                                    </Grid>

                                    <Grid size={8}>
                                        <span >
                                            {t("copytrade.auth.password")}
                                        </span>
                                    </Grid>
                                    <Grid size={16}>
                                        <PinInput
                                            autoFocus={autoFocus}
                                            value={!disabledAuth ? pinCA : "•••••••"}
                                            disabled={disabledAuth}
                                            onChange={handleChangePinCA}
                                            toggleShowPin={toggleShowPin}
                                            showPin={showPin}
                                        />
                                    </Grid>
                                </>
                                }
                            </Grid>
                        )}

                        {authType === TradeAuthType.SMARTOTP && (
                            <Grid container alignItems={"center"} spacing={2} columns={24} >
                                <Grid size={8}>
                                    <span>
                                        Smart OTP
                                    </span>
                                </Grid>
                                <Grid size={16}>
                                    <OtpInput
                                        autoFocus={autoFocus}
                                        value={!disabledAuth ? smartOtp : "•••••••"}
                                        onChange={handleChangeSmartOtp}
                                        disabled={disabledAuth}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {!noSaveAuth && !disableSaveAuth && verified === false && (
                            (authType === TradeAuthType.CTS && currentCA === null) ? null :
                                <Grid container alignItems={"center"} spacing={2} columns={24}>
                                    <Grid size={8}>
                                        <span>
                                            {t("copytrade.common.saveVerify")}
                                        </span>
                                    </Grid>
                                    <Grid size={12}>
                                        <span>
                                            <Switch
                                                checked={microAppSaveAuthentication}
                                                defaultChecked={saveVerify}
                                                onChange={handleChangeMicroAppSaveAuthStatus}
                                                inputProps={{ 'aria-label': 'controlled' }}
                                            />
                                        </span>
                                    </Grid>
                                </Grid>
                        )}

                        {/* Button mặc định của TransAuth2 */}
                        {!noButton && (
                            <Grid container alignItems={"center"} spacing={2} columns={24}>
                                <Grid size={8}>
                                    <ButtonElement onClick={onCancel}>
                                        {t("copytrade.common.cancel")}
                                    </ButtonElement>
                                </Grid>
                                <Grid size={16}>
                                    <ButtonElement
                                        disabled={!smartOtp?.trim() && !pin?.trim() && !otp?.trim()}
                                        onClick={processAuth}
                                    >
                                        {t("copytrade.common.confirm")}
                                    </ButtonElement>
                                </Grid>
                            </Grid>
                        )}

                        {
                            verified === true && <Stack
                                spacing={1}
                                direction={"row"}
                                alignItems={'center'}
                                justifyContent={'center'}
                            >
                                <CheckCircleOutlineIcon sx={{ fontSize: "30px" }} color="success" />
                                <TypographyElement ColorElement="brand" >
                                    {t('copytrade.common.verified')}
                                </TypographyElement>
                            </Stack>
                        }

                    </Stack>
                </div>
            </Fragment>
        );
    }
);

export default TransAuth;